import { IBusinessRule, IBusinessRuleItemSpecifier } from '../types/brandMenu';
import {
  BusinessRuleCategory,
  BusinessRuleOption,
} from '../constants/brandMenu';
import logger from './logger';
import { CartItem } from './cart';
import { UnavailableItemsByName } from '../redux/features/menuUnification/menuUnification.props';
import { isBMModifierAnUnavailableConstraint } from './menuUnification.utils';
import { getParentModifier } from './menu';

export interface OptionReplacementInfo {
  shouldApplyReplacement: boolean;
  targetModifierGroups: Array<{
    modifierGroupName: string;
    optionName: string;
  }>;
}

/**
 * Extracts the base size from a combo size option (e.g., "small combo" -> "small")
 */
export const extractBaseSize = (sizeOption: string): string | null => {
  const suffixes = [' combo', ' side', ' drink', ' size'];
  for (const suffix of suffixes) {
    if (sizeOption.toLowerCase().endsWith(suffix)) {
      return sizeOption.slice(0, -suffix.length).trim();
    }
  }
  return null;
};

const normalizeName = (value: string): string => value.toLowerCase().trim();

/**
 * Generic function to match rule patterns (items or constraints) against candidates.
 * Supports wildcard matching with '*' pattern.
 * @param normalizeFn - Function to normalize strings for comparison
 */
export const matchesRulePattern = (
  patterns: Array<string | { name?: string | null } | null | undefined>,
  candidates: Array<string | null | undefined>,
  emptyPatternBehavior: 'allow' | 'deny',
  normalizeFn: (value: string) => string = normalizeName
): boolean => {
  if (!patterns || patterns.length === 0) {
    return emptyPatternBehavior === 'allow';
  }

  const normalizedCandidates = candidates
    .filter((candidate): candidate is string => !!candidate)
    .map((candidate) => normalizeFn(candidate));

  // Handle case where candidate is null/undefined - only match wildcard
  if (normalizedCandidates.length === 0) {
    return patterns.some((pattern) => {
      if (!pattern) {
        return false;
      }
      const patternValue = typeof pattern === 'string' ? pattern : pattern.name;
      return patternValue === '*';
    });
  }

  return patterns.some((pattern) => {
    if (!pattern) {
      return false;
    }

    const patternValue = typeof pattern === 'string' ? pattern : pattern.name;

    if (!patternValue) {
      return false;
    }

    if (patternValue === '*') {
      return true;
    }

    return normalizedCandidates.includes(normalizeFn(patternValue));
  });
};

const matchesRuleItems = (
  items: IBusinessRule['items_to_apply'],
  candidates: Array<string | null | undefined>
): boolean => {
  return matchesRulePattern(items, candidates, 'deny');
};

const hasWildcardItem = (items: IBusinessRule['items_to_apply']): boolean => {
  if (!items || items.length === 0) {
    return false;
  }

  return items.some(({ name }) => name === '*');
};

const matchesRuleConstraints = (
  constraints: IBusinessRule['constraints_to_apply'],
  candidate: string | null
): boolean => {
  return matchesRulePattern(constraints, [candidate], 'allow');
};

/**
 * Finds the selected combo size option name by matching against if.options from combo size transformation rules
 * Instead of using hardcoded COMBO_SIZE_MODIFIER_NAME_PREFIX, we use the rule's if.options to find matches
 * @param comboSizeTransformationRules - Pre-filtered combo size transformation rules
 * @returns The name of the selected combo size option, or undefined if not found
 */
const getSelectedComboSize = (
  cartItem: CartItem,
  comboSizeTransformationRules: IBusinessRule[]
): string | undefined => {
  if (comboSizeTransformationRules.length === 0) {
    logger.log(
      `getSelectedComboSize: No combo size transformation rules found for cart item "${cartItem.name}"`
    );
    return undefined;
  }

  // Collect all if.options from combo size transformation rules
  const comboSizeOptions = new Set<string>();
  comboSizeTransformationRules.forEach((rule) => {
    rule.if.options.forEach((option) => {
      if (option !== '*') {
        // Normalize and add to set
        comboSizeOptions.add(normalizeName(option));
      }
    });
  });

  if (comboSizeOptions.size === 0) {
    // If all rules use '*', we can't determine which options are combo sizes
    logger.log(
      `getSelectedComboSize: No combo size options found (all rules use '*') for cart item "${cartItem.name}"`
    );
    return undefined;
  }

  // Search through childModifierGroups for selected items that match combo size options
  // Only check childModifierGroups as that's where selected items are stored
  for (const childGroup of Object.values(cartItem.childModifierGroups || {})) {
    const selectedItems = Object.values(childGroup.selectedItems || {});

    for (const selectedItem of selectedItems) {
      const normalizedName = normalizeName(selectedItem.name);
      if (comboSizeOptions.has(normalizedName)) {
        return selectedItem.name;
      }
    }
  }

  return undefined;
};

/**
 * Finds a matching default size selection business rule
 * Matches rules based on if.options instead of constraints_to_apply
 */
const findMatchingDefaultSizeRule = (
  itemName: string,
  selectedOption: string,
  defaultSizeRules: IBusinessRule[]
): IBusinessRule | null => {
  if (!defaultSizeRules || defaultSizeRules.length === 0) {
    return null;
  }

  return (
    defaultSizeRules.find((rule) => {
      // Only match default size selection rules
      if (
        rule.items_to_apply.length > 0 &&
        !matchesRuleItems(rule.items_to_apply, [itemName, selectedOption])
      ) {
        return false;
      }

      // Match rules based on if.options instead of constraints_to_apply
      // Check if the selected option matches any of the rule's if.options
      // "*" means match any option
      const normalizedSelectedOption = normalizeName(selectedOption);
      return rule.if.options.some(
        (option) =>
          option === '*' || normalizeName(option) === normalizedSelectedOption
      );
    }) || null
  );
};

/**
 * Finds all matching combo size transformation business rules
 * Matches rules based on if.options instead of constraints_to_apply
 * @param comboSizeTransformationRules - Pre-filtered combo size transformation rules
 * @returns Array of all matching rules
 */
const findMatchingComboSizeTransformationRules = (
  itemName: string,
  selectedOption: string,
  comboSizeTransformationRules: IBusinessRule[]
): IBusinessRule[] => {
  if (
    !comboSizeTransformationRules ||
    comboSizeTransformationRules.length === 0
  ) {
    return [];
  }

  return comboSizeTransformationRules.filter((rule) => {
    if (
      rule.items_to_apply.length > 0 &&
      !matchesRuleItems(rule.items_to_apply, [itemName])
    ) {
      return false;
    }

    // Match rules based on if.options instead of constraints_to_apply
    // Check if the selected option matches any of the rule's if.options
    // "*" means match any option
    const normalizedSelectedOption = normalizeName(selectedOption);
    return rule.if.options.some(
      (option) =>
        option === '*' || normalizeName(option) === normalizedSelectedOption
    );
  });
};

/**
 * Returns the first default-size rule that applies to a target modifier group,
 * taking into account the current selection context and child item state.
 */
const findDefaultRuleForTarget = (
  defaultSizeRules: IBusinessRule[],
  {
    targetName,
    hasExistingSelection,
    normalizedSelectedOptionName,
    selectedChildItems,
    contextModifierGroupName,
  }: {
    targetName: string;
    hasExistingSelection: boolean;
    normalizedSelectedOptionName: string | null;
    selectedChildItems: Array<{ name: string; modifierGroupName: string }>;
    contextModifierGroupName?: string | null;
  }
): IBusinessRule | undefined => {
  return defaultSizeRules.find((rule) => {
    if (!rule.then.target.includes(targetName)) {
      return false;
    }

    const ruleItems = rule.items_to_apply;
    const wildcardItem = hasWildcardItem(ruleItems);

    if (wildcardItem) {
      return !normalizedSelectedOptionName && !hasExistingSelection;
    }

    if (!ruleItems || ruleItems.length === 0) {
      return false;
    }

    const normalizedRuleItems = ruleItems
      .filter(
        (item): item is IBusinessRuleItemSpecifier =>
          !!item?.name && item.name !== '*'
      )
      .map((item) => normalizeName(item.name));

    // First, try to match based on what the user JUST selected (selectedContext)
    if (normalizedSelectedOptionName) {
      const matchesSelectedOption = normalizedRuleItems.includes(
        normalizedSelectedOptionName
      );

      if (matchesSelectedOption) {
        const matchesIfOption = rule.if.options.some((option: string) => {
          if (option === '*') {
            return true;
          }
          return normalizeName(option) === normalizedSelectedOptionName;
        });

        if (matchesIfOption) {
          return true;
        }
      }
      // Fall through to evaluate selected child items if the chosen option
      // does not satisfy the rule's conditions.
    }

    /**
     * If the just-selected option doesn't match, check ALL selected items across the cart.
     * This allows rules to match based on OTHER modifier groups' selections.
     *
     * Example rule: "If user selected 'burger' in Main Item, then default Drinks to 'medium'"
     * - selectedContext might be "Drinks" → "small drink" (doesn't match rule)
     * - But selectedChildItems contains { name: "burger", modifierGroupName: "Main Item" }
     * - This matches the rule's items_to_apply → rule applies!
     *
     * We exclude items from the context modifier group to avoid matching the trigger itself.
     */
    return selectedChildItems.some((childItem) => {
      // Don't match items from the context group (the one that triggered this evaluation)
      if (
        contextModifierGroupName &&
        normalizeName(childItem.modifierGroupName) ===
          normalizeName(contextModifierGroupName)
      ) {
        return false;
      }

      const normalizedChildName = normalizeName(childItem.name);
      return normalizedRuleItems.includes(normalizedChildName);
    });
  });
};

type ComboTargetState = {
  modifierGroup: CartItem['modifierGroups'][string];
  selectedItems: CartItem[];
  hasExistingSelection: boolean;
};

type DefaultSizeEvaluation = {
  defaultRule: IBusinessRule | undefined;
  enforceDefaultOptions: string[];
};

/**
 * Constructs the current combo target state, including the modifier definition
 * and any existing selections, if the modifier group is present on the cart item.
 */
const buildComboTargetState = (
  cartItem: CartItem,
  targetName: string
): ComboTargetState | null => {
  const modifierGroup = Object.values(cartItem.modifierGroups || {}).find(
    (group) => group.name === targetName
  );

  if (!modifierGroup) {
    logger.log(
      `Combo size transformation: Modifier group "${targetName}" not found on cart item "${cartItem.name}"`
    );
    return null;
  }

  const cartModifierGroup = modifierGroup.id
    ? cartItem.childModifierGroups?.[modifierGroup.id]
    : undefined;
  const selectedItems = Object.values(cartModifierGroup?.selectedItems || {});

  return {
    modifierGroup,
    selectedItems,
    hasExistingSelection: selectedItems.length > 0,
  };
};

const evaluateDefaultRuleForComboTarget = (
  targetName: string,
  state: ComboTargetState,
  defaultSizeRules: IBusinessRule[],
  options: {
    selectedContext?: {
      modifierGroupName: string;
      optionName: string;
    };
    selectedChildItems: Array<{ name: string; modifierGroupName: string }>;
  }
): DefaultSizeEvaluation => {
  const { selectedContext, selectedChildItems } = options;

  const isContextTarget =
    !!selectedContext &&
    normalizeName(selectedContext.modifierGroupName) ===
      normalizeName(targetName);

  const normalizedSelectedOptionName = selectedContext
    ? normalizeName(selectedContext.optionName)
    : null;

  const defaultRule = isContextTarget
    ? undefined
    : findDefaultRuleForTarget(defaultSizeRules, {
        targetName,
        hasExistingSelection: state.hasExistingSelection,
        normalizedSelectedOptionName,
        selectedChildItems,
        contextModifierGroupName: selectedContext?.modifierGroupName || null,
      });

  const enforceDefaultOptions = defaultRule
    ? [...defaultRule.then.target_options]
    : [];

  return {
    defaultRule,
    enforceDefaultOptions,
  };
};

const resolveComboSizeTransformationTargets = (
  cartItem: CartItem,
  baseSize: string,
  transformationRule: IBusinessRule,
  defaultSizeRules: IBusinessRule[],
  unavailableItemsByName: UnavailableItemsByName,
  selectedContext?: {
    modifierGroupName: string;
    optionName: string;
  }
): Array<{ modifierGroupName: string; optionName: string }> => {
  const normalizedBaseSize = normalizeName(baseSize);
  const targetModifierGroups: Array<{
    modifierGroupName: string;
    optionName: string;
  }> = [];

  /**
   * Collect ALL currently selected items across ALL modifier groups in the cart item.
   * This snapshot is used to match default rules based on what's already selected
   * in OTHER modifier groups (not just the one that triggered this evaluation).
   *
   * Example: If cart has:
   *   - Main Item: "burger" (selected)
   *   - Drinks: "medium drink" (selected)
   *   - Sides: (empty)
   * Then selectedChildItems = [
   *   { name: "burger", modifierGroupName: "Main Item" },
   *   { name: "medium drink", modifierGroupName: "Drinks" }
   * ]
   */
  const selectedChildItems = Object.values(
    cartItem.childModifierGroups || {}
  ).flatMap((group) =>
    Object.values(group.selectedItems || {}).map((item) => ({
      name: item.name,
      modifierGroupName: group.name,
    }))
  );

  transformationRule.then.target.forEach((targetName) => {
    const state = buildComboTargetState(cartItem, targetName);

    if (!state) {
      return;
    }

    const candidateItems = Object.values(state.modifierGroup.menuItems || {});

    if (!candidateItems.length) {
      logger.log(
        `Combo size transformation: No menu items found in modifier group "${targetName}" for cart item "${cartItem.name}"`
      );
      return;
    }

    const matchingItem =
      candidateItems.find((item) =>
        normalizeName(item.name).startsWith(`${normalizedBaseSize} `)
      ) ||
      candidateItems.find((item) =>
        normalizeName(item.name).includes(normalizedBaseSize)
      );

    if (!matchingItem) {
      logger.log(
        `Combo size transformation: No option containing size "${baseSize}" found in modifier group "${targetName}" for cart item "${cartItem.name}"`
      );
      return;
    }

    const alreadySelected = state.selectedItems.some(
      (item) => normalizeName(item.name) === normalizeName(matchingItem.name)
    );

    if (alreadySelected) {
      logger.log(
        `Combo size transformation: Target "${targetName}" already selected as "${matchingItem.name}", skipping`
      );
      return;
    }

    // Check if item is unavailable via constraints or menu item availability
    const isUnavailableByConstraint = isBMModifierAnUnavailableConstraint(
      cartItem,
      unavailableItemsByName,
      getParentModifier(matchingItem.pathToItem || ''),
      matchingItem.pathToItem || '',
      state.modifierGroup.id,
      matchingItem.itemId
    );
    const isUnavailableByMenuItem = matchingItem.available === false;
    const isUnavailable = isUnavailableByConstraint || isUnavailableByMenuItem;

    if (isUnavailable) {
      logger.log(
        `Combo size transformation: Target "${targetName}" not available", evaluating default size rule`
      );
      const defaultEvaluation = evaluateDefaultRuleForComboTarget(
        targetName,
        state,
        defaultSizeRules,
        {
          selectedContext,
          selectedChildItems,
        }
      );

      if (defaultEvaluation.enforceDefaultOptions.length > 0) {
        defaultEvaluation.enforceDefaultOptions.forEach((optionName) => {
          const alreadyQueued = targetModifierGroups.some(
            (queued) =>
              queued.modifierGroupName === targetName &&
              queued.optionName === optionName
          );

          if (!alreadyQueued) {
            targetModifierGroups.push({
              modifierGroupName: targetName,
              optionName,
            });
          }
        });

        if (defaultEvaluation.defaultRule) {
          logger.log(
            `Combo size transformation: Ensured default size selection via rule "${defaultEvaluation.defaultRule.name}" for modifier group "${targetName}"`
          );
        }
      }
      return;
    }

    targetModifierGroups.push({
      modifierGroupName: targetName,
      optionName: matchingItem.name,
    });
  });

  return targetModifierGroups;
};

/**
 * Applies combo size transformation rules to determine target modifier groups.
 * Returns replacement info if combo size transformation is applicable, null otherwise.
 */
const applyComboSizeRules = (
  modifierGroupName: string,
  selectedMenuItemName: string,
  cartItem: CartItem,
  comboSizeTransformationRules: IBusinessRule[],
  defaultSizeRules: IBusinessRule[],
  unavailableItemsByName: UnavailableItemsByName
): OptionReplacementInfo | null => {
  // Check if the selected option matches any combo size transformation rule's if.options
  const isSelectedOptionComboSize = comboSizeTransformationRules.some(
    (rule) => {
      const normalizedSelectedOption = normalizeName(selectedMenuItemName);
      return rule.if.options.some(
        (option) =>
          option === '*' || normalizeName(option) === normalizedSelectedOption
      );
    }
  );

  // Get the effective combo size name
  // If the selected option is a combo size, use it directly; otherwise, don't use combo size transformation values
  const effectiveComboSizeName = isSelectedOptionComboSize
    ? selectedMenuItemName
    : false;

  // Try combo size transformation rules first (if applicable)
  if (!effectiveComboSizeName) {
    return null;
  }

  // Try to find all matching combo size transformation rules using the effective combo size name
  const matchingComboSizeTransformationRules =
    findMatchingComboSizeTransformationRules(
      cartItem.name,
      effectiveComboSizeName,
      comboSizeTransformationRules
    );

  if (matchingComboSizeTransformationRules.length === 0) {
    return null;
  }

  const baseSize = extractBaseSize(effectiveComboSizeName);

  if (!baseSize) {
    return null;
  }

  // Loop through each matching rule and return early when we find the target modifier groups
  for (const comboSizeTransformationRule of matchingComboSizeTransformationRules) {
    const targetModifierGroups = resolveComboSizeTransformationTargets(
      cartItem,
      baseSize,
      comboSizeTransformationRule,
      defaultSizeRules,
      unavailableItemsByName,
      isSelectedOptionComboSize
        ? undefined
        : {
            modifierGroupName,
            optionName: selectedMenuItemName,
          }
    );

    if (targetModifierGroups.length > 0) {
      logger.log(
        `Combo size transformation applied for "${selectedMenuItemName}" (combo size "${effectiveComboSizeName}") using rule "${comboSizeTransformationRule.name}" targeting ${targetModifierGroups.length} modifier groups`
      );
      return {
        shouldApplyReplacement: true,
        targetModifierGroups,
      };
    }
  }

  const ruleNames = matchingComboSizeTransformationRules
    .map((rule) => rule.name)
    .join(', ');
  logger.log(
    `Combo size transformation rules "${ruleNames}" produced no targets for item "${cartItem.name}" and base size "${baseSize}"`
  );

  return null;
};

/**
 * Applies default size rules to determine target modifier groups.
 * Only applies to targets that have no existing selection.
 * Returns replacement info if default size rule is applicable, null otherwise.
 */
const applyDefaultSizeRules = (
  selectedMenuItemName: string,
  cartItem: CartItem,
  defaultSizeRules: IBusinessRule[]
): OptionReplacementInfo | null => {
  const matchingDefaultRule = findMatchingDefaultSizeRule(
    cartItem.name,
    selectedMenuItemName,
    defaultSizeRules
  );

  if (!matchingDefaultRule) {
    return null;
  }

  const targets = matchingDefaultRule.then.target;

  const targetModifierGroups: Array<{
    modifierGroupName: string;
    optionName: string;
  }> = [];

  if (matchingDefaultRule.then.target_options.length > 0) {
    // For each target, if it exists in the cart item and has no selection,
    // apply all target_options to it
    targets.forEach((target) => {
      const state = buildComboTargetState(cartItem, target);
      if (state && !state.hasExistingSelection) {
        matchingDefaultRule.then.target_options.forEach((targetOption) => {
          logger.log(
            `Default size rule applied: "${target}" -> "${targetOption}" (target has no existing selection)`
          );
          targetModifierGroups.push({
            modifierGroupName: target,
            optionName: targetOption,
          });
        });
      }
    });
  }

  if (targetModifierGroups.length > 0) {
    logger.log(
      `Default size rule applied: ${selectedMenuItemName} -> applying to ${targetModifierGroups.length} modifier groups`
    );
    return {
      shouldApplyReplacement: true,
      targetModifierGroups,
    };
  }

  return null;
};

/**
 * Analyze if option replacement should be applied based on modifier selection
 * Uses business rules from the menu JSON exclusively (no config fallback)
 */
export const analyzeOptionReplacement = (
  modifierGroupName: string,
  selectedMenuItemName: string,
  cartItem: CartItem,
  businessRules: IBusinessRule[],
  unavailableItemsByName: UnavailableItemsByName
): OptionReplacementInfo => {
  if (!businessRules || businessRules.length === 0) {
    return { shouldApplyReplacement: false, targetModifierGroups: [] };
  }

  const defaultSizeRules = businessRules.filter(
    (rule) =>
      rule.category === BusinessRuleCategory.DefaultSizeSelection &&
      rule.then.operation === BusinessRuleOption.SelectDefaultSize
  );

  // Get combo size transformation rules to find selected combo size
  // Check combo size transformation rules FIRST, before default size rules
  const comboSizeTransformationRules = businessRules.filter(
    (rule) =>
      rule.category === BusinessRuleCategory.ComboSizeTransformation &&
      rule.then.operation === BusinessRuleOption.TransformComboSize
  );

  // Try combo size transformation rules first
  const comboResult = applyComboSizeRules(
    modifierGroupName,
    selectedMenuItemName,
    cartItem,
    comboSizeTransformationRules,
    defaultSizeRules,
    unavailableItemsByName
  );

  if (comboResult) {
    return comboResult;
  }

  // Apply default size rules only if combo size transformation rules produced no targets
  const defaultResult = applyDefaultSizeRules(
    selectedMenuItemName,
    cartItem,
    defaultSizeRules
  );

  if (defaultResult) {
    return defaultResult;
  }

  return { shouldApplyReplacement: false, targetModifierGroups: [] };
};
